import { useMutation, useQueryClient } from "react-query";
import { TOpenModalConfiguracoes } from ".."
import { useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { TCCOConfiguracoes } from "../../../../../../types/api/financeiro/conta-corrente-consumo-configuracoes";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, TextField, FormHelperText } from "@mui/material";
import CCOFiltrosConfig from "./FiltrosConfig";
import CCOGerarTitulosReceberConfig from "./GerarTitulosReceberConfig";
import CCOGerarTitulosPagarConfig from "./GerarTitulosPagarConfig";
import CCORegistroRecebimentoConfig from "./RegistroRecebimentoConfig";
import CCORegistroPagamentoConfig from "./RegistroPagamentoConfig";

export default function DialogCriacaoEditCCOConfiguracoes({
    open,
    defaultValue,
    handleClose
}: DialogCriacaoEditCCOConfiguracoesProps) {

    const queryClient = useQueryClient();

    const isEdit = useMemo(() => Boolean(defaultValue?.edit), [defaultValue])
    const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue?.defaultValue : null, [defaultValue])

    const methods = useForm<TCCOConfiguracoes>({
        defaultValues: {
            nome: valoresIniciais?.nome || "",
            filtros: valoresIniciais?.filtros || null,
            status: valoresIniciais?.status || "Ativo",
            frequencia_fechamento: valoresIniciais?.frequencia_fechamento || "Mensal",
            momento_fechamento: valoresIniciais?.momento_fechamento || "Fim",
            config_geracao_titulo_receber: valoresIniciais?.config_geracao_titulo_receber || {},
            config_geracao_titulo_pagar: valoresIniciais?.config_geracao_titulo_pagar || {},
            config_registro_recebimento: valoresIniciais?.config_registro_recebimento || {},
            config_registro_pagamento: valoresIniciais?.config_registro_pagamento || {}
        },
    })
    const { control, handleSubmit, getFieldState, formState: { errors } } = methods;

    //Querys e mutations
    const { mutate: criarConfiguracao, isLoading: isLoadingCriarConfig } = useMutation({
        mutationKey: 'cadastrar-cco-configuracao',
        mutationFn: (inputs: any) => axiosClient.post("financeiro/conta-corrente-consumo/cadastrar-configuracao", inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao cadastrar departamento!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-cco-configuracoes"])
            handleClose()
            enqueueSnackbar('Cadastro de configuração realizado com sucesso!', { variant: 'success' });
        }
    })

    const { mutate: atualizarConfiguracao, isLoading: isLoadingAttConfig } = useMutation({
        mutationKey: 'atualizar-cco-configuracao',
        mutationFn: (inputs: any) => axiosClient.put(`financeiro/conta-corrente-consumo/atualizar-configuracao/${valoresIniciais?.id}`, inputs),
        onError: (error) => {
            console.error(error)
            enqueueSnackbar('Erro ao atualizar configuracao!', { variant: 'error' });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["pesquisar-cco-configuracoes"])
            handleClose()
            enqueueSnackbar('Atualização de configuracao feita com sucesso!', { variant: 'success' });
        }
    })
    //

    //Funções manipuladoras
    const onSubmit = (data: TCCOConfiguracoes) => {
        const params = {
            nome: data.nome,
            filtros: data.filtros,
            status: data.status,
            frequencia_fechamento: data.frequencia_fechamento,
            momento_fechamento: data.momento_fechamento,
            config_geracao_titulo_receber: data.config_geracao_titulo_receber,
            config_geracao_titulo_pagar: data.config_geracao_titulo_pagar,
            config_registro_recebimento: data.config_registro_recebimento,
            config_registro_pagamento: data.config_registro_pagamento
        }
        if (isEdit) {
            atualizarConfiguracao(params)
        }
        if (!isEdit) {
            criarConfiguracao(params)
        }
    }
    //

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                scroll="body"
            >
                <DialogTitle>
                    {`${!isEdit ? "Adicionar" : "Editar"} configurações de fechamento`}
                </DialogTitle>

                {
                    (isLoadingAttConfig || isLoadingCriarConfig) &&
                    <Box display="flex" justifyContent="center" mt={4} mb={8}>
                        <CircularProgress />
                    </Box>
                }

                {
                    !isLoadingAttConfig && !isLoadingCriarConfig &&
                    <>
                        <Box
                            component={'form'}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid size={{xs: 8}}>
                                        <Controller
                                            name="nome"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    defaultValue={valoresIniciais?.nome}
                                                    value={field.value}
                                                    required
                                                    label="Nome"
                                                    variant="outlined"
                                                    fullWidth
                                                    error={getFieldState('nome').invalid}
                                                    helperText={getFieldState('nome').error?.message || ""}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 4}}>
                                        <Controller
                                            name="status"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl
                                                    required
                                                    fullWidth
                                                    error={getFieldState('status').invalid}
                                                >
                                                    <InputLabel id="status_label">Status</InputLabel>
                                                    <Select
                                                        {...field}
                                                        defaultValue={valoresIniciais?.status || ""}
                                                        label="Status"
                                                        labelId="status_label"
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        {statusValues.map((status) => (
                                                            <MenuItem key={status} value={status}>
                                                                {status}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {getFieldState('status').error && (
                                                        <FormHelperText error>
                                                            {getFieldState('status').error?.message}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 8}}>
                                        <Controller
                                            name="frequencia_fechamento"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl
                                                    required
                                                    fullWidth
                                                    error={getFieldState('frequencia_fechamento').invalid}
                                                >
                                                    <InputLabel id="frequencia_fechamento_label">Frequência fechamento</InputLabel>
                                                    <Select
                                                        {...field}
                                                        defaultValue={valoresIniciais?.frequencia_fechamento || ""}
                                                        label="Frequência fechamento"
                                                        labelId="frequencia_fechamento_label"
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        {frequenciaValues.map((value) => (
                                                            <MenuItem key={value} value={value}>
                                                                {value}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {getFieldState('frequencia_fechamento').error && (
                                                        <FormHelperText error>
                                                            {getFieldState('frequencia_fechamento').error?.message}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 4}}>
                                        <Controller
                                            name="momento_fechamento"
                                            control={control}
                                            rules={{ required: 'Campo obrigatório' }}
                                            render={({ field }) => (
                                                <FormControl
                                                    required
                                                    fullWidth
                                                    error={getFieldState('momento_fechamento').invalid}
                                                >
                                                    <InputLabel id="momento_fechamento_label">Momento fechamento</InputLabel>
                                                    <Select
                                                        {...field}
                                                        defaultValue={valoresIniciais?.momento_fechamento || ""}
                                                        label="Momento fechamento"
                                                        labelId="momento_fechamento_label"
                                                        variant="outlined"
                                                        fullWidth
                                                    >
                                                        {momentoFechamentoValues.map((value) => (
                                                            <MenuItem key={value} value={value}>
                                                                {value}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {getFieldState('momento_fechamento').error && (
                                                        <FormHelperText error>
                                                            {getFieldState('momento_fechamento').error?.message}
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <CCOFiltrosConfig />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <CCOGerarTitulosReceberConfig />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <CCORegistroRecebimentoConfig />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <CCOGerarTitulosPagarConfig />
                                    </Grid>
                                    <Grid size={{xs: 12}}>
                                        <CCORegistroPagamentoConfig />
                                    </Grid>
                                </Grid>
                                <Box mt={2} width={'100%'} display={'flex'} justifyContent={'flex-end'} alignItems={'center'} gap={2}>
                                    <Button
                                        onClick={handleClose}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        autoFocus
                                        disabled={isLoadingCriarConfig || isLoadingAttConfig}
                                    >
                                        {
                                            (isLoadingCriarConfig || isLoadingAttConfig) &&
                                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                                        }
                                        {!isEdit ? "Adicionar" : "Atualizar"}
                                    </Button>
                                </Box>
                            </DialogContent>
                        </Box>
                    </>
                }
            </Dialog>
        </FormProvider>
    )
}


interface DialogCriacaoEditCCOConfiguracoesProps {
    open: boolean,
    defaultValue: TOpenModalConfiguracoes
    handleClose: () => void
}

const statusValues = ["Ativo", "Inativo"]

const frequenciaValues = ["Mensal", "Semanal"]

const momentoFechamentoValues = ["Fim", "Inicio"]